import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import SearchableSelect from '../Common/SearchableSelect';
import InstrumentPicker from '../Common/InstrumentPicker';

const INPUT = 'w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
const LABEL = 'block text-xs font-medium text-dark-300 mb-1';

export default function InstrumentForm({ onInstrumentCreated, onClose }) {
  // Identificação
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [sector, setSector] = useState('');
  const [instrumentClassId, setInstrumentClassId] = useState('');
  const [tipoInstrumento, setTipoInstrumento] = useState('');
  const [localCalibracao, setLocalCalibracao] = useState('');
  const [operationalStatus, setOperationalStatus] = useState('ativo');
  const [criticity, setCriticity] = useState('medium');
  const [calibratable, setCalibratable] = useState(true);
  const [calibrationFrequencyDays, setCalibrationFrequencyDays] = useState('');

  // Identificação técnica
  const [marca, setMarca] = useState('');
  const [modelo, setModelo] = useState('');
  const [numeroDeSerie, setNumeroDeSerie] = useState('');
  const [partNumber, setPartNumber] = useState('');

  // Metrologia
  const [resolucao, setResolucao] = useState('');
  const [exatidao, setExatidao] = useState('');
  const [tipoFaixa, setTipoFaixa] = useState('');
  const [modoComparacao, setModoComparacao] = useState('');
  const [codigoEquipamentoSuperior, setCodigoEquipamentoSuperior] = useState('');

  // Faixas
  const [usageMin, setUsageMin] = useState('');
  const [usageMax, setUsageMax] = useState('');
  const [usageUnit, setUsageUnit] = useState('');
  const [nominalMin, setNominalMin] = useState('');
  const [nominalMax, setNominalMax] = useState('');
  const [nominalUnit, setNominalUnit] = useState('');

  const [classificacao, setClassificacao] = useState('instrumento');
  const [instrumentoPaiId, setInstrumentoPaiId] = useState('');
  const [instrumentosPai, setInstrumentosPai] = useState([]);

  const [sectors, setSectors] = useState([]);
  const [classes, setClasses] = useState([]);
  const [freqFromClass, setFreqFromClass] = useState(false);
  const [tagError, setTagError] = useState('');
  const [tagValid, setTagValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadSectors(); loadClasses(); loadInstrumentosPai(); }, []);

  const loadSectors = async () => {
    try {
      const res = await api.get('/instruments/sectors');
      setSectors(res.data);
    } catch (err) { console.error(err); }
  };

  const loadInstrumentosPai = async () => {
    try {
      const res = await api.get('/instruments/tags');
      setInstrumentosPai(res.data || []);
    } catch (err) { console.error(err); }
  };

  const handleCreateSector = async (name) => {
    await api.post('/instruments/sectors', { name });
    await loadSectors();
  };

  const loadClasses = async () => {
    try {
      const res = await api.get('/instrument-classes');
      setClasses(res.data);
    } catch (err) { console.error(err); }
  };

  const validateTag = async (value) => {
    if (!value.trim()) { setTagError(''); setTagValid(false); return; }
    try {
      const res = await api.get(`/instruments?tag=${encodeURIComponent(value)}`);
      const list = res.data.dados || res.data;
      if (list.length > 0) { setTagError('TAG já cadastrada'); setTagValid(false); }
      else { setTagError(''); setTagValid(true); }
    } catch (err) { console.error(err); }
  };

  const tagTimer = useRef(null);
  const handleTagChange = (e) => {
    const value = e.target.value;
    setTag(value);
    setTagError('');
    setTagValid(false);
    clearTimeout(tagTimer.current);
    if (value.trim()) {
      tagTimer.current = setTimeout(() => validateTag(value), 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!tagValid) { setError('Corrija a TAG antes de salvar'); return; }
    setLoading(true);
    try {
      const payload = {
        tag, description, sector,
        classificacao,
        instrumentoPaiId: instrumentoPaiId || undefined,
        instrumentClassId: instrumentClassId || undefined,
        tipoInstrumento: tipoInstrumento || undefined,
        localCalibracao: localCalibracao || undefined,
        operationalStatus, criticity, calibratable,
        calibrationFrequencyDays: calibratable && calibrationFrequencyDays ? parseInt(calibrationFrequencyDays) : undefined,
        marca: marca || undefined, modelo: modelo || undefined,
        numeroDeSerie: numeroDeSerie || undefined, partNumber: partNumber || undefined,
        ...(calibratable && {
          resolucao: resolucao || undefined, exatidao: exatidao || undefined,
          tipoFaixa: tipoFaixa || undefined, modoComparacao: modoComparacao || undefined,
          codigoEquipamentoSuperior: codigoEquipamentoSuperior || undefined,
          usageRange: usageMin || usageMax ? { min: parseFloat(usageMin) || 0, max: parseFloat(usageMax) || 0, unit: usageUnit } : undefined,
          nominalRange: nominalMin || nominalMax ? { min: parseFloat(nominalMin) || 0, max: parseFloat(nominalMax) || 0, unit: nominalUnit } : undefined,
        }),
      };
      const res = await api.post('/instruments', payload);
      if (onInstrumentCreated) onInstrumentCreated(res.data);
      if (onClose) onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar instrumento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-dark-800 border border-dark-600 rounded-xl p-6 text-white max-h-[85vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-lg font-semibold">Novo Instrumento</h3>
        {onClose && <button type="button" onClick={onClose} className="text-dark-400 hover:text-white">✕</button>}
      </div>

      {/* Classificação */}
      <div className="flex gap-2 mb-4">
        {[
          { val: 'instrumento', label: 'Instrumento' },
          { val: 'padrao', label: 'Padrão de Referência' },
        ].map(op => (
          <button
            key={op.val}
            type="button"
            onClick={() => setClassificacao(op.val)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
              classificacao === op.val
                ? 'bg-blue-700 border-blue-600 text-white'
                : 'bg-dark-700 border-dark-500 text-dark-400 hover:text-white'
            }`}
          >
            {op.val === 'padrao' ? '🔬 ' : '📏 '}{op.label}
          </button>
        ))}
      </div>

      {/* Equipamento pai */}
      <div className="mb-4">
        <label className={LABEL}>Equipamento Pai (opcional)</label>
        <select
          value={instrumentoPaiId}
          onChange={e => setInstrumentoPaiId(e.target.value)}
          className={INPUT}
        >
          <option value="">— Nenhum —</option>
          {instrumentosPai.map(i => (
            <option key={i._id || i.id} value={i._id || i.id}>{i.tag} — {i.description}</option>
          ))}
        </select>
        <p className="text-xs text-dark-500 mt-1">Ex: seladora que contém este manômetro/termopar</p>
      </div>

      {/* Identificação */}
      <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Identificação</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className={LABEL}>TAG *</label>
          <input type="text" value={tag} onChange={handleTagChange} placeholder="Ex: TI-001, FIT-1234, TAG-A"
            className={`${INPUT} ${tagError ? 'border-red-500 focus:ring-red-500' : tagValid ? 'border-green-500 focus:ring-green-500' : ''}`} required />
          {tagError && <p className="text-red-400 text-xs mt-1">{tagError}</p>}
        </div>
        <div>
          <label className={LABEL}>Classe</label>
          <select
            value={instrumentClassId}
            onChange={e => {
              const id = e.target.value;
              setInstrumentClassId(id);
              const cls = classes.find(c => c._id === id);
              if (cls?.calibrationFrequencyDays && !calibrationFrequencyDays) {
                setCalibrationFrequencyDays(String(cls.calibrationFrequencyDays));
                setFreqFromClass(true);
              } else if (!id) {
                if (freqFromClass) { setCalibrationFrequencyDays(''); setFreqFromClass(false); }
              }
            }}
            className={INPUT}
          >
            <option value="">— Sem classe —</option>
            {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="mb-3">
        <label className={LABEL}>Descrição Completa *</label>
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={INPUT} required />
      </div>

      <div className="mb-3">
        <label className={LABEL}>Setor *</label>
        <SearchableSelect options={sectors} value={sector} onChange={setSector} placeholder="Digite para buscar..." onCreateOption={handleCreateSector} />
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label className={LABEL}>Status Operacional *</label>
          <select value={operationalStatus} onChange={(e) => setOperationalStatus(e.target.value)} className={INPUT} required>
            <option value="ativo">Ativo</option>
            <option value="desativado">Desativado</option>
            <option value="backup">Backup</option>
            <option value="manutencao">Manutenção</option>
          </select>
        </div>
        <div>
          <label className={LABEL}>Criticidade</label>
          <select value={criticity} onChange={(e) => setCriticity(e.target.value)} className={INPUT}>
            <option value="high">Alta</option>
            <option value="medium">Média</option>
            <option value="low">Baixa</option>
          </select>
        </div>
        <div className="flex flex-col justify-center">
          <label className={LABEL}>Calibrável</label>
          <button
            type="button"
            onClick={() => setCalibratable(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
              calibratable
                ? 'bg-emerald-900/30 border-emerald-700/50 text-emerald-400'
                : 'bg-dark-700 border-dark-500 text-dark-400'
            }`}
          >
            <span className={`w-3 h-3 rounded-full ${calibratable ? 'bg-emerald-400' : 'bg-dark-400'}`} />
            {calibratable ? 'Sim' : 'Não'}
          </button>
        </div>
      </div>

      {calibratable && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <label className={LABEL} style={{ margin: 0 }}>Frequência de Calibração (dias)</label>
            {freqFromClass && (
              <span className="text-[10px] text-blue-400 bg-blue-950/30 border border-blue-800/40 px-2 py-0.5 rounded-full">
                herdado da classe
              </span>
            )}
          </div>
          <input
            type="number" min="1" value={calibrationFrequencyDays}
            onChange={e => { setCalibrationFrequencyDays(e.target.value); setFreqFromClass(false); }}
            placeholder="Ex: 365" className={INPUT}
          />
          {(() => {
            const cls = classes.find(c => c._id === instrumentClassId);
            return cls?.criterioAceitacao ? (
              <p className="text-[10px] text-emerald-400 mt-1">✓ Critério da classe: {cls.criterioAceitacao}</p>
            ) : null;
          })()}
        </div>
      )}

      {/* Identificação Técnica */}
      <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Identificação Técnica</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className={LABEL}>Marca / Fabricante</label>
          <input type="text" value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Ex: Fluke" className={INPUT} />
        </div>
        <div>
          <label className={LABEL}>Modelo</label>
          <input type="text" value={modelo} onChange={(e) => setModelo(e.target.value)} placeholder="Ex: 87V" className={INPUT} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className={LABEL}>Número de Série</label>
          <input type="text" value={numeroDeSerie} onChange={(e) => setNumeroDeSerie(e.target.value)} className={INPUT} />
        </div>
        <div>
          <label className={LABEL}>Part Number</label>
          <input type="text" value={partNumber} onChange={(e) => setPartNumber(e.target.value)} className={INPUT} />
        </div>
      </div>

      {calibratable && (
        <>
          {/* Características Metrológicas */}
          <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3 mt-5">Características Metrológicas</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={LABEL}>Resolução</label>
              <input type="text" value={resolucao} onChange={(e) => setResolucao(e.target.value)} placeholder="Ex: 0,01 °C" className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Exatidão / Classe</label>
              <input type="text" value={exatidao} onChange={(e) => setExatidao(e.target.value)} placeholder="Ex: ±0,5%" className={INPUT} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className={LABEL}>Tipo de Instrumento</label>
              <select value={tipoInstrumento} onChange={(e) => setTipoInstrumento(e.target.value)} className={INPUT}>
                <option value="">— selecione —</option>
                <option value="portatil">Portátil</option>
                <option value="fixo">Fixo</option>
                <option value="componente_malha">Componente de Malha</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Local de Calibração</label>
              <select value={localCalibracao} onChange={(e) => setLocalCalibracao(e.target.value)} className={INPUT}>
                <option value="">— selecione —</option>
                <option value="in_loco">In loco (lab vem ao local)</option>
                <option value="laboratorio">Laboratório (equipamento enviado)</option>
                <option value="ambos">Ambos</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Tipo de Faixa</label>
              <select value={tipoFaixa} onChange={(e) => setTipoFaixa(e.target.value)} className={INPUT}>
                <option value="">— selecione —</option>
                <option value="linear">Linear</option>
                <option value="por_relacao">Por Relação</option>
                <option value="inversa">Inversa</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Modo de Comparação</label>
              <select value={modoComparacao} onChange={(e) => setModoComparacao(e.target.value)} className={INPUT}>
                <option value="">— selecione —</option>
                <option value="fixo">Fixo</option>
                <option value="variavel">Variável</option>
              </select>
            </div>
          </div>
          <div className="mb-5">
            <label className={LABEL}>Instrumento Pai (TAG)</label>
            <InstrumentPicker
              value={codigoEquipamentoSuperior}
              onChange={setCodigoEquipamentoSuperior}
              excludeTag={tag}
              placeholder="Digite a TAG para buscar..."
            />
          </div>

          {/* Faixas */}
          <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Faixas de Operação</p>
          <div className="mb-3">
            <p className="text-xs text-dark-400 mb-2">Faixa de Uso</p>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" step="any" placeholder="Min" value={usageMin} onChange={e => setUsageMin(e.target.value)} className={INPUT} />
              <input type="number" step="any" placeholder="Max" value={usageMax} onChange={e => setUsageMax(e.target.value)} className={INPUT} />
              <input type="text" placeholder="Unidade" value={usageUnit} onChange={e => setUsageUnit(e.target.value)} className={INPUT} />
            </div>
          </div>
          <div className="mb-5">
            <p className="text-xs text-dark-400 mb-2">Faixa Nominal</p>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" step="any" placeholder="Min" value={nominalMin} onChange={e => setNominalMin(e.target.value)} className={INPUT} />
              <input type="number" step="any" placeholder="Max" value={nominalMax} onChange={e => setNominalMax(e.target.value)} className={INPUT} />
              <input type="text" placeholder="Unidade" value={nominalUnit} onChange={e => setNominalUnit(e.target.value)} className={INPUT} />
            </div>
          </div>
        </>
      )}

      {error && <p className="text-red-400 mb-4 text-sm">{error}</p>}

      <button type="submit" disabled={loading}
        className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
        {loading ? 'Salvando...' : 'Salvar Instrumento'}
      </button>
    </form>
  );
}
